<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { routerPush } from '@/router'

import { api, isFetchError } from '@/services'
import type { LoginAccount } from '@/services/api'

import { useUserStore } from '@/stores/user'
import { useGlobalStore } from '@/stores/global'
import ErrorDisplayHelper from '@/components/helpers/ErrorDisplayHelper.vue'

interface Props {
  isModal?: boolean
}

const props = defineProps<Props>()
const isModal = ref(props.isModal ?? false)

const emit = defineEmits(['close'])

const formRef = ref<HTMLFormElement | null>(null)
const loading = ref<boolean | undefined>()
const showPassword = ref<boolean | undefined>()

const form: LoginAccount = reactive({
  email: '',
  password: ''
})

const { updateAuth, updateUser } = useUserStore()

const globalStore = useGlobalStore()

const haveErrors = computed(() => {
  return errors.value?.length > 0
})
const errors = ref()

const required = async (v: string) => {
  return !!v || 'Field is required'
}

const login = async () => {
  // errors.value = {}

  if (!formRef.value?.checkValidity()) {
    return
  }

  try {
    globalStore.setLoading(true)
    const result = await api.api.loginAccount({ data: form })

    updateUser(result.data.data?.user)
    api.setSecurityData(result.data.authorization?.access_token)
    updateAuth(result.data.authorization)

    if (isModal.value) {
      emit('close')
    }

    await routerPush('home')
  } catch (error) {
    if (isFetchError(error)) {
      if (error.status === 401) {
        errors.value = [
          {
            message: 'Invalid login credentials'
          }
        ]
      } else if (error.status === 403) {
        errors.value = [
          {
            message: error.error.error
          },
          {
            message: error.error.message
          },
          {
            message: error.error.data['resend_verification_link']
          }
        ]
      } else {
        errors.value = error.error?.errors
      }
    }
    // console.error(error)
  } finally {
    globalStore.setLoading(false)
  }
}

const dynamicClasses = computed(() => {
  let classes = 'dialog-form'
  if (isModal.value) {
    classes += ' dialog-modal'
  }
  return classes
})
</script>
<template>
  <v-card class="ma-0 pa-0 mx-auto" :class="dynamicClasses" variant="flat">
    <ErrorDisplayHelper :have-errors="haveErrors" :errors="errors"></ErrorDisplayHelper>
    <v-form ref="formRef" @submit.prevent="login">
      <v-text-field
        v-model="form.email"
        :readonly="loading"
        :rules="[required]"
        class="mb-2"
        label="Email"
        prepend-inner-icon="mdi-email-outline"
        variant="outlined"
        density="compact"
      ></v-text-field>

      <v-text-field
        v-model="form.password"
        :readonly="loading"
        :rules="[required]"
        label="Password"
        placeholder="Enter your password"
        :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
        prepend-inner-icon="mdi-lock-outline"
        variant="outlined"
        density="compact"
        :type="showPassword ? 'text' : 'password'"
        hint="At least 8 characters"
        @click:append-inner="showPassword = !showPassword"
      ></v-text-field>

      <br />

      <v-btn
        :disabled="!formRef"
        :loading="loading"
        color="success"
        size="large"
        type="submit"
        variant="elevated"
        block
      >
        Sign In
      </v-btn>
    </v-form>
  </v-card>
</template>
